/**
 * Parent Feedback (Phase 8 / Module 8) validation contracts.
 *
 * Mirrors the schema in `packages/db/src/schema/feedback.ts`. Used by both
 * the API route handlers (input validation) and any downstream type sharing.
 *
 * Locked v1 axes — five Likert-5 questions, optional 300-char comment.
 * Cross-axis weighting is a TMA concern; this schema only carries the
 * per-axis integer scores.
 *
 * Spec: docs/deep-research-report4.md §"Parent Feedback Collection".
 */
import { z } from 'zod';
import { idSchema, paginationSchema } from './common.js';

// ----- Axes ---------------------------------------------------------------

/**
 * The canonical, ordered list of feedback axes. v1 ships these five; new
 * axes can be added without a schema migration since `question_set` is
 * stored as JSON text. Importers should treat this list as authoritative.
 */
export const feedbackAxes = [
  'clarity',
  'patience',
  'communication',
  'progress',
  'overall',
] as const;
export type FeedbackAxis = (typeof feedbackAxes)[number];

/** Likert 1–5 integer scale, hard-bounded — values outside fail validation. */
const likertScoreSchema = z.number().int().min(1).max(5);

const periodKeySchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/u, 'periodKey must be in YYYY-MM format');

const commentSchema = z.string().trim().min(1).max(300);

// ----- Submission write payloads -----------------------------------------

/**
 * The five-axis responses object. Marked `strict()` so an attacker can't
 * smuggle an unknown axis past the validator — every key must be a known
 * axis and every value must be a 1–5 integer.
 */
export const feedbackResponsesSchema = z
  .object({
    clarity: likertScoreSchema,
    patience: likertScoreSchema,
    communication: likertScoreSchema,
    progress: likertScoreSchema,
    overall: likertScoreSchema,
  })
  .strict();
export type FeedbackResponses = z.infer<typeof feedbackResponsesSchema>;

/**
 * POST /api/me/feedback/submissions — parent submit.
 *
 * `clientFirstRequestAt` is an OPTIONAL client-supplied ISO timestamp of
 * when the parent first opened the form (used by the speed anti-gaming
 * flag). When absent, the speed flag is skipped.
 */
export const feedbackSubmissionCreateSchema = z
  .object({
    formId: idSchema,
    teacherId: idSchema,
    batchId: idSchema.nullable().optional(),
    studentId: idSchema,
    responses: feedbackResponsesSchema,
    comment: commentSchema.optional(),
    source: z.enum(['portal', 'mobile']).optional(),
    clientFirstRequestAt: z.string().datetime({ offset: true }).optional(),
  })
  .strict();
export type FeedbackSubmissionCreateInput = z.infer<
  typeof feedbackSubmissionCreateSchema
>;

/**
 * PATCH /api/me/feedback/submissions/:id — parent edit within the form's
 * edit window. The edit overwrites the full response set; partial edits
 * would muddle the speed/straight-line anti-gaming heuristics.
 */
export const feedbackSubmissionUpdateSchema = z
  .object({
    responses: feedbackResponsesSchema,
    comment: commentSchema.nullable().optional(),
    clientFirstRequestAt: z.string().datetime({ offset: true }).optional(),
  })
  .strict();
export type FeedbackSubmissionUpdateInput = z.infer<
  typeof feedbackSubmissionUpdateSchema
>;

// ----- Admin moderation --------------------------------------------------

export const feedbackModerationStatusSchema = z.enum([
  'clear',
  'flagged',
  'hidden',
]);
export type FeedbackModerationStatus = z.infer<
  typeof feedbackModerationStatusSchema
>;

export const feedbackModerationSchema = z
  .object({
    moderationStatus: feedbackModerationStatusSchema,
    note: z.string().trim().min(1).max(1000).optional(),
  })
  .strict();
export type FeedbackModerationInput = z.infer<typeof feedbackModerationSchema>;

export const feedbackAdminListQuerySchema = paginationSchema.extend({
  teacherId: idSchema.optional(),
  householdId: idSchema.optional(),
  studentId: idSchema.optional(),
  periodKey: periodKeySchema.optional(),
  moderationStatus: feedbackModerationStatusSchema.optional(),
  /**
   * Scope submissions (and export rows) to a single form. Added in PR12
   * for the Forms detail page's Submissions panel.
   */
  formId: idSchema.optional(),
});
export type FeedbackAdminListQuery = z.infer<
  typeof feedbackAdminListQuerySchema
>;

/**
 * Manual recompute trigger body. All filters are optional and AND-compose:
 *   - formId omitted → recompute for every active form
 *   - teacherId / periodKey omitted → expand across the distinct tuples in
 *     `feedback_submissions` matching the rest of the filter.
 *
 * `formId` was added in PR12 so the Forms detail page can target a single
 * form when versioning. Older callers that only pass `periodKey`/`teacherId`
 * continue to work unchanged (all three fields are optional).
 */
export const feedbackRecomputeSchema = z
  .object({
    periodKey: periodKeySchema.optional(),
    teacherId: idSchema.optional(),
    formId: idSchema.optional(),
  })
  .strict();
export type FeedbackRecomputeInput = z.infer<typeof feedbackRecomputeSchema>;

/**
 * POST /api/feedback/admin/submissions/:id/withdraw — super_admin overrides
 * the parent edit-window and soft-deletes the submission. The note is
 * mandatory (HR governance — every override must be justified) and is
 * stored in the audit row, not on the submission itself, since the schema
 * has no withdrawal_note column.
 */
export const submissionAdminWithdrawSchema = z
  .object({
    note: z.string().trim().min(2).max(2000),
  })
  .strict();
export type SubmissionAdminWithdrawInput = z.infer<
  typeof submissionAdminWithdrawSchema
>;

/**
 * GET /api/feedback/admin/aggregates query — all filters optional and
 * AND-compose. Returns the projected aggregate row set replacing the
 * previous 500-row client-side computation in `_authed.hr.feedback.tsx`.
 */
export const feedbackAdminAggregatesQuerySchema = z
  .object({
    formId: idSchema.optional(),
    periodKey: periodKeySchema.optional(),
    teacherId: idSchema.optional(),
    batchId: idSchema.optional(),
  })
  .strict();
export type FeedbackAdminAggregatesQuery = z.infer<
  typeof feedbackAdminAggregatesQuerySchema
>;

// ----- Forms management --------------------------------------------------

/**
 * Single question definition inside `feedback_forms.question_set`. v1 ships
 * five of these (one per axis); future cadences can extend the list without
 * touching this schema.
 */
export const feedbackQuestionSchema = z
  .object({
    axis: z.enum(feedbackAxes),
    prompt: z.string().trim().min(2).max(300),
    scaleMin: z.number().int().min(1).max(5),
    scaleMax: z.number().int().min(1).max(5),
    scaleLabels: z.array(z.string().trim().min(1).max(40)).min(2).max(10),
  })
  .strict()
  .refine((q) => q.scaleMin < q.scaleMax, {
    message: 'scaleMin must be less than scaleMax',
    path: ['scaleMin'],
  })
  .refine((q) => q.scaleLabels.length === q.scaleMax - q.scaleMin + 1, {
    message: 'scaleLabels length must equal (scaleMax - scaleMin + 1)',
    path: ['scaleLabels'],
  });
export type FeedbackQuestion = z.infer<typeof feedbackQuestionSchema>;

const feedbackQuestionSetSchema = z
  .array(feedbackQuestionSchema)
  .min(1)
  .max(20)
  .refine(
    (qs) => new Set(qs.map((q) => q.axis)).size === qs.length,
    'question_set axes must be unique',
  );

export const feedbackFormCreateSchema = z
  .object({
    title: z.string().trim().min(2).max(160),
    scope: z.string().trim().min(2).max(40).default('teacher_monthly'),
    cadence: z.string().trim().min(2).max(40).default('monthly'),
    questionSet: feedbackQuestionSetSchema,
    activeFrom: z.string().datetime({ offset: true }).nullable().optional(),
    activeTo: z.string().datetime({ offset: true }).nullable().optional(),
    isActive: z.boolean().default(true),
    minVisibleResponseCount: z.number().int().min(1).max(100).default(5),
    editWindowHours: z.number().int().min(1).max(720).default(24),
  })
  .strict();
export type FeedbackFormCreateInput = z.infer<typeof feedbackFormCreateSchema>;

export const feedbackFormUpdateSchema = z
  .object({
    title: z.string().trim().min(2).max(160).optional(),
    scope: z.string().trim().min(2).max(40).optional(),
    cadence: z.string().trim().min(2).max(40).optional(),
    questionSet: feedbackQuestionSetSchema.optional(),
    activeFrom: z.string().datetime({ offset: true }).nullable().optional(),
    activeTo: z.string().datetime({ offset: true }).nullable().optional(),
    isActive: z.boolean().optional(),
    minVisibleResponseCount: z.number().int().min(1).max(100).optional(),
    editWindowHours: z.number().int().min(1).max(720).optional(),
  })
  .strict();
export type FeedbackFormUpdateInput = z.infer<typeof feedbackFormUpdateSchema>;

// ----- Teacher/aggregate read query --------------------------------------

export const feedbackAggregateQuerySchema = z
  .object({
    periodKey: periodKeySchema.optional(),
    batchId: idSchema.optional(),
  })
  .strict();
export type FeedbackAggregateQuery = z.infer<
  typeof feedbackAggregateQuerySchema
>;

// ----- Response shapes (informational; not enforced server-side) ---------

const aggregateRowSchema = z.object({
  id: z.string(),
  formId: z.string(),
  periodKey: z.string(),
  teacherId: z.string(),
  batchId: z.string().nullable(),
  responsesCount: z.number().int().min(0),
  clarityAvgX100: z.number().int().min(0),
  patienceAvgX100: z.number().int().min(0),
  communicationAvgX100: z.number().int().min(0),
  progressAvgX100: z.number().int().min(0),
  overallAvgX100: z.number().int().min(0),
  weightedScoreX100: z.number().int().min(0),
  visibleToTeacher: z.boolean(),
  lastRecomputedAt: z.string(),
});

export const feedbackAggregateResponseSchema = z.discriminatedUnion(
  'insufficient',
  [
    z.object({
      insufficient: z.literal(false),
      aggregate: aggregateRowSchema,
      perBatch: z.array(aggregateRowSchema),
      submissions: z
        .array(
          z.object({
            id: z.string(),
            householdId: z.string(),
            studentId: z.string(),
            studentName: z.string().nullable(),
            batchId: z.string().nullable(),
            responses: feedbackResponsesSchema,
            comment: z.string().nullable(),
            moderationStatus: feedbackModerationStatusSchema,
            submittedAt: z.string(),
            editedAt: z.string().nullable(),
          }),
        )
        .optional(),
    }),
    z.object({
      insufficient: z.literal(true),
      responsesCount: z.number().int().min(0),
      minVisibleResponseCount: z.number().int().min(1),
    }),
  ],
);
export type FeedbackAggregateResponse = z.infer<
  typeof feedbackAggregateResponseSchema
>;
